// Encabezado/pie estandarizado del Anexo 1 MSP (AM 00115-2021 + Fe de errata
// RO 454) — compartido por los 10 formularios 010A/010B/012A/012B/013A/013B/
// 118/122/054/055 (packages/hce-chart únicamente). Construido ENCIMA de
// `crearCursorPdf()` (lib/pdfLayout.ts, no tocar ese archivo — ya es el
// bottleneck reusado por ~50 formularios): solo usa su API pública
// (texto/etiqueta/espacio/linea), sin acceso a `page`/`y` directo.
//
// `etiqueta()` no envuelve texto largo (silenciosamente se sale de la
// página) — por eso acá SIEMPRE se usa como leyenda corta (mayúsculas,
// tracking manual) y el VALOR real siempre va con `texto()` en la línea de
// abajo, que sí envuelve. Mismo patrón de 2 líneas (etiqueta+texto) que ya
// usa `pdfFormularioGenerico.ts` para "Paciente"/"Profesional"/"Fecha".
import type { PDFDocument, PDFFont } from 'pdf-lib';
import type { CursorPdf } from './layout';
import { MARGIN, MUTED, NAVY, PAGE_H, PAGE_W } from './layout';
import { lineaContactoConsultorio } from './datosConsultorioHeader';

export type UnidadEdadMsp = 'H' | 'D' | 'M' | 'A';

export interface EdadMsp {
  valor: number;
  unidad: UnidadEdadMsp;
}

const ETIQUETA_UNIDAD_EDAD: Record<UnidadEdadMsp, string> = { H: 'horas', D: 'días', M: 'meses', A: 'años' };

export interface OpcionesEdadMsp {
  /** El Excel del HCU-116 (Certificado de Discapacidad) solo trae columnas M/A, no H/D — con esto, cualquier edad menor a un mes se redondea a "0 meses" en vez de bajar a días/horas. */
  readonly soloMesesAnios?: boolean;
}

/** Edad en H(oras)/D(ías)/M(eses)/A(ños), formato que usa el Anexo 1 MSP para HCU (relevante sobre todo en neonatología/pediatría). `undefined` si no hay fecha de nacimiento válida. */
export function edadMsp(birthDate: string | undefined, opts: OpcionesEdadMsp = {}): EdadMsp | undefined {
  if (!birthDate) return undefined;
  const nacimiento = new Date(birthDate);
  if (Number.isNaN(nacimiento.getTime())) return undefined;
  const ahora = new Date();
  const horas = Math.floor((ahora.getTime() - nacimiento.getTime()) / (1000 * 60 * 60));
  if (horas < 0) return undefined;
  if (!opts.soloMesesAnios) {
    if (horas < 24) return { valor: horas, unidad: 'H' };
    const dias = Math.floor(horas / 24);
    if (dias < 30) return { valor: dias, unidad: 'D' };
  }
  let meses = (ahora.getFullYear() - nacimiento.getFullYear()) * 12 + (ahora.getMonth() - nacimiento.getMonth());
  if (ahora.getDate() < nacimiento.getDate()) meses--;
  meses = Math.max(0, meses);
  if (meses < 24) return { valor: meses, unidad: 'M' };
  let anios = ahora.getFullYear() - nacimiento.getFullYear();
  const aunNoCumple = ahora.getMonth() < nacimiento.getMonth() || (ahora.getMonth() === nacimiento.getMonth() && ahora.getDate() < nacimiento.getDate());
  if (aunNoCumple) anios--;
  return { valor: anios, unidad: 'A' };
}

export function formatoEdadMsp(edad: EdadMsp): string {
  return `${edad.valor} ${ETIQUETA_UNIDAD_EDAD[edad.unidad]}`;
}

/** `SNS-MSP/HCU-form.{código}/2021` — leyenda legal impresa en los 10 formularios del Anexo 1, EXCEPTO 054/055 (HCU 2008, ver `citaHcu2008`). */
export function citaLegalMsp(codigo: string): string {
  return `SNS-MSP/HCU-form.${codigo}/2021`;
}

/**
 * Leyendas de origen de los formularios que NO tienen página en el PDF oficial de la HCU 2021 (AC-00115-2021).
 * Ver docs/formularios-propios-vs-msp.md: ninguno de estos se rotula como HCU 2021.
 */
export const CITA_059 = 'SNS-MSP-DNCSS-Form-059-2016 (código DNCSS-MSP-002), Manual de Seguridad del Paciente, AM 00000115 del 17-oct-2016';
/** 054/055: formularios de la HCU 2008. Sin afirmar vigencia. */
export function citaHcu2008(codigo: string): string {
  return `HCU 2008, AM 000138 del 14-mar-2008, formulario ${codigo}; no figura en el listado de la HCU 2021`;
}
/** Leyenda de los formularios propios de LogiaCare (034, SOAPIE, escalas esc-*): declara que NO son del Anexo 1 y no cita ninguna norma. */
export const FUENTE_PROPIO = 'Formulario propio de LogiaCare; no es un formulario del Anexo 1 del MSP.';
/** FEMO: diseño propio; no es del Anexo 1. No imprime el AM 00025-2025: no hay fuente primaria en `docs/` que lo confirme (la insignia en pantalla sí lo rotula como base). */
export const FUENTE_FEMO = 'Evaluación médica ocupacional generada por LogiaCare conforme al Acuerdo Ministerial 00025-2025 (Registro Oficial 125, 16-09-2025); no es un formulario del Anexo 1 del MSP.';
export const FUENTE_034 = FUENTE_PROPIO;
/** Códigos sin fuente oficial que imprimen `FUENTE_PROPIO` (misma lista que `lib/formulariosPropios.ts` de hce-chart, tipo 'propio'). */
const CODIGOS_PROPIOS_PDF: ReadonlySet<string> = new Set(['034', 'enf-soapie', 'esc-phq9', 'esc-nyha', 'esc-glasgow']);

/** Leyenda de origen impresa al pie de los PDF de questionnaire/pares (059 y los propios); `undefined` para el resto. */
export function fuenteFormularioPdf(codigo: string): string | undefined {
  if (codigo === '059') return CITA_059;
  if (CODIGOS_PROPIOS_PDF.has(codigo)) return FUENTE_PROPIO;
  return undefined;
}

export interface DatosEncabezadoMsp {
  institucion: string;
  unicodigo?: string;
  establecimiento?: string;
  numeroHcu?: string;
  numeroArchivo?: string;
  hoja: string;
  pacienteNombre: string;
  /** Cédula/identificación del paciente — opcional; hoy solo la manda el 024 (su PDF anterior, modo questionnaire, la imprimía). */
  pacienteIdentificacion?: string;
  pacienteSexo?: string;
  pacienteEdad?: EdadMsp;
  /** ISO (yyyy-mm-dd) — se imprime tal cual, sin re-formatear. */
  pacienteFechaNacimiento?: string;
  especialidad: string;
  codigo: string;
  tituloFormulario: string;
  /** 054/055 llevan `citaHcu2008(codigo)` (no son de la HCU 2021); `dibujarPieMsp` no dibuja nada si falta. */
  citaLegal?: string;
  /** Etiqueta del campo de identificación del profesional en el pie del PDF — por defecto "Registro MSP/ACESS" (`generarPdfFormularioMsp`). El HCU-117 pide "Cédula", no código ACESS. */
  etiquetaIdentificacion?: string;
  /** Dirección/teléfono/correo del consultorio (pedido de Sue, 2026-09-14) — el servidor los completa desde el tenant de la sesión, nunca el navegador. */
  direccionConsultorio?: string;
  telefonoConsultorio?: string;
  correoConsultorio?: string;
  /**
   * Certificados (117, 116-cert): el documento debe caber en UNA hoja (pedido de Sue, 2026-09-18). Usa el
   * encabezado y los pares compactos (`dibujarEncabezadoMspCompacto`, una línea "pregunta: respuesta").
   */
  unaHoja?: boolean;
  /** Servicio que captura el propio formulario (012A/012B: Emergencia / Consulta externa / Hospitalización). Sin él, el encabezado sigue diciendo "Consulta externa" como siempre. */
  servicio?: string;
  /** 116-cert: el bloque A del papel no trae servicio ni especialidad — el encabezado no los imprime (el resto de formularios siempre los imprime). */
  sinServicioNiEspecialidad?: boolean;
  /** El papel del formulario no trae servicio y 'Consulta externa' sería engañoso (hospitalización, referencia, paliativos...): el encabezado imprime la especialidad pero NO el servicio. `servicio` explícito gana si viene. */
  sinServicio?: boolean;
}

/**
 * "Hoja n de N" en la esquina superior derecha de CADA página del PDF generado (2026-09-18, pedido de Sue:
 * el encabezado decía "Hoja: 1" aunque el documento saliera en 2 hojas). Reemplaza al campo de texto libre
 * `datos.hoja`, que ya no se imprime. Se llama una vez, al final, cuando ya se sabe el total de páginas.
 */
export function numerarHojasMsp(doc: PDFDocument, font: PDFFont): void {
  const paginas = doc.getPages();
  paginas.forEach((pagina, i) => {
    const texto = `Hoja ${i + 1} de ${paginas.length}`;
    const size = 8;
    pagina.drawText(texto, { x: PAGE_W - MARGIN - font.widthOfTextAtSize(texto, size), y: PAGE_H - MARGIN + 20, size, font, color: MUTED });
  });
}

const servicioDe = (datos: DatosEncabezadoMsp): string => datos.servicio || 'Consulta externa';
const servicioVisible = (datos: DatosEncabezadoMsp): boolean => !datos.sinServicioNiEspecialidad && (!datos.sinServicio || Boolean(datos.servicio));

/** Variante compacta del encabezado para los certificados de una sola hoja: mismos datos, en pocas líneas. */
export function dibujarEncabezadoMspCompacto(cursor: CursorPdf, datos: DatosEncabezadoMsp): void {
  cursor.texto(datos.tituloFormulario, { serif: true, size: 15, color: NAVY });
  cursor.espacio(2);
  cursor.linea();
  // Sin el rótulo "Institución:" (pedido de Sue, 2026-09-18): la razón social sola ya se entiende.
  cursor.texto(datos.institucion || '—', { bold: true, size: 10 });
  const contacto = lineaContactoConsultorio({ direccion: datos.direccionConsultorio, telefono: datos.telefonoConsultorio, correo: datos.correoConsultorio });
  if (contacto) cursor.texto(contacto, { size: 8, color: MUTED });
  const sitio = [datos.unicodigo ? `Unicódigo: ${datos.unicodigo}` : '', datos.establecimiento ? `Establecimiento: ${datos.establecimiento}` : ''].filter(Boolean).join(' · ');
  if (sitio) cursor.texto(sitio, { size: 9 });
  cursor.espacio(2);
  cursor.linea();
  cursor.texto(`Paciente: ${datos.pacienteNombre}${datos.pacienteIdentificacion ? ` — C.I. ${datos.pacienteIdentificacion}` : ''}`, { bold: true, size: 11 });
  const datosPaciente = [
    datos.numeroHcu ? `N° HCU ${datos.numeroHcu}` : undefined,
    datos.pacienteSexo,
    datos.pacienteEdad ? formatoEdadMsp(datos.pacienteEdad) : undefined,
    datos.pacienteFechaNacimiento ? `Nacimiento ${datos.pacienteFechaNacimiento}` : undefined,
  ]
    .filter(Boolean)
    .join(' · ');
  if (datosPaciente) cursor.texto(datosPaciente, { size: 9, color: MUTED });
  const atencion = [
    datos.numeroArchivo ? `N° de archivo: ${datos.numeroArchivo}` : '',
    datos.sinServicioNiEspecialidad ? '' : `Especialidad: ${datos.especialidad || '—'}`,
    servicioVisible(datos) ? `Servicio: ${servicioDe(datos)}` : '',
  ]
    .filter(Boolean)
    .join(' · ');
  if (atencion) cursor.texto(atencion, { size: 9 });
  cursor.espacio(4);
}

/** Encabezado estándar: título (ya trae el código — `formularios.f010a.titulo` = "010A - Laboratorio Clínico Solicitud", nunca se le vuelve a anteponer `codigo`), institución/unicódigo/establecimiento, identificación del paciente (incluye N° HCU y edad H-D-M-A), N° de archivo/hoja, especialidad y "SERVICIO: Consulta externa" (litera fijo — estos formularios nunca son de hospitalización/cama). */
export function dibujarEncabezadoMsp(cursor: CursorPdf, datos: DatosEncabezadoMsp): void {
  cursor.texto(datos.tituloFormulario, { serif: true, size: 18, color: NAVY });
  cursor.espacio(4);
  cursor.linea();

  // Sin el rótulo "Institución" (pedido de Sue, 2026-09-18, mismo criterio que el encabezado compacto de los
  // certificados): la razón social sola. La receta médica tiene su propio encabezado y no se toca.
  cursor.texto(datos.institucion || '—', { bold: true, size: 11 });
  cursor.espacio(4);

  const contactoConsultorio = lineaContactoConsultorio({
    direccion: datos.direccionConsultorio,
    telefono: datos.telefonoConsultorio,
    correo: datos.correoConsultorio,
  });
  if (contactoConsultorio) {
    cursor.texto(contactoConsultorio, { size: 8.5, color: MUTED });
    cursor.espacio(4);
  }

  if (datos.unicodigo) {
    cursor.etiqueta('Unicódigo');
    cursor.texto(datos.unicodigo, { size: 10 });
    cursor.espacio(3);
  }
  if (datos.establecimiento) {
    cursor.etiqueta('Establecimiento');
    cursor.texto(datos.establecimiento, { size: 10 });
    cursor.espacio(3);
  }

  cursor.espacio(4);
  cursor.linea();

  cursor.etiqueta('Paciente');
  cursor.texto(datos.pacienteIdentificacion ? `${datos.pacienteNombre} — C.I. ${datos.pacienteIdentificacion}` : datos.pacienteNombre, { bold: true, size: 12 });
  const datosPaciente = [
    datos.numeroHcu ? `N° HCU ${datos.numeroHcu}` : undefined,
    datos.pacienteSexo,
    datos.pacienteEdad ? formatoEdadMsp(datos.pacienteEdad) : undefined,
    datos.pacienteFechaNacimiento ? `Nacimiento ${datos.pacienteFechaNacimiento}` : undefined,
  ]
    .filter(Boolean)
    .join(' · ');
  if (datosPaciente) cursor.texto(datosPaciente, { size: 10, color: MUTED });
  cursor.espacio(6);

  if (datos.numeroArchivo) {
    cursor.etiqueta('N° de archivo');
    cursor.texto(datos.numeroArchivo, { size: 10 });
    cursor.espacio(3);
  }
  cursor.espacio(4);

  if (!datos.sinServicioNiEspecialidad) {
    cursor.etiqueta('Especialidad');
    cursor.texto(datos.especialidad || '—', { size: 10 });
    cursor.espacio(3);
    if (servicioVisible(datos)) {
      cursor.etiqueta('Servicio');
      cursor.texto(servicioDe(datos), { size: 10 });
    }
  }

  cursor.espacio(8);
  cursor.linea();
}

/** Pie con la leyenda legal (`citaLegal`: cita del Anexo 1, o HCU 2008 en 054/055) — no dibuja nada si `datos.citaLegal` está ausente. */
export function dibujarPieMsp(cursor: CursorPdf, datos: DatosEncabezadoMsp): void {
  if (!datos.citaLegal) return;
  cursor.espacio(10);
  cursor.linea();
  cursor.texto(datos.citaLegal, { size: 8, color: MUTED });
}
